import type { HydraCollection, PartialHydraCollection } from "@verbleif/lib/src/composables/Api/Type/hydra.interface";
import type { HydraSource, ItemSource } from "@verbleif/lib/src/composables/Query/hydraSource";
import { paramsToKey } from "@verbleif/lib/src/composables/Query/hydraInfinite";
import { api } from "../Api/api";
import { itemKey } from "./resourceKeys";

export function defineHydraSource<T>(
  key: readonly unknown[],
  url: string,
  buildParams?: (params: URLSearchParams) => void,
): HydraSource<T> {
  const extras = new URLSearchParams();
  buildParams?.(extras);
  const extraKey = extras.size > 0 ? paramsToKey(extras) : null;
  return {
    key: extraKey ? [...key, extraKey] : key,
    load: async (params, signal) => {
      const next = new URLSearchParams(params);
      buildParams?.(next);
      const { data } = await api.get<HydraCollection<T> | PartialHydraCollection<T>>(url, { params: next, signal });
      return data;
    },
  };
}

/**
 * Query parameters (e.g. a read option such as `installableFor`) extend the key below `itemKey(iri)`, so an
 * invalidation of the IRI still reaches every variant of the item.
 */
export function defineItemSource<T>(iri: string, params?: URLSearchParams): ItemSource<T> {
  const paramsKey = params && params.size > 0 ? itemParamsKey(params) : null;
  return {
    key: paramsKey ? [...itemKey(iri), paramsKey] : itemKey(iri),
    load: async (signal) => {
      const { data } = await api.get<T>(iri, paramsKey ? { signal, params } : { signal });
      return data;
    },
  };
}

// Every param shapes an item response, so none is dropped from the key (paramsToKey drops the collection-only
// `page` / `partial`).
function itemParamsKey(params: URLSearchParams): string {
  const copy = new URLSearchParams(params);
  copy.sort();
  return copy.toString();
}
